import React, { useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";

const SECTION_LINKS = [
  { id: "overview", label: "Overview" },
  { id: "engines", label: "Dual Engines" },
  { id: "flow", label: "Lifecycle" },
  { id: "trust", label: "Trust Layer" },
];

const DisplayReceiptIr = () => {
  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
          }
        });
      },
      { threshold: 0.12 },
    );

    const elements = document.querySelectorAll(".reveal");
    elements.forEach((el) => io.observe(el));

    return () => io.disconnect();
  }, []);
  return (
    <>
      <Navbar sectionLinks={SECTION_LINKS} onSectionSelect={scrollToSection} />

      <main id="top">
        <section className="wrap hero-display section-loose reveal">
          <div className="hero-copy">
            <div className="eyebrow">Display IR & Receipt IR</div>
            <h1>From shelf and receipt to retail truth.</h1>
            <p>
              Kartini converts unstructured retail evidence into operationally trusted, explainable
              decisions. Display IR validates the physical shelf. Receipt IR validates the financial
              transaction. Together, they create a complete promotional intelligence loop.
            </p>
            <p>
              Built on computer vision, document intelligence, master data alignment, fraud defense
              and business rules, Kartini goes beyond simple image detection to produce auditable
              retail intelligence that can be used confidently in real operations.
            </p>
            <div className="hero-actions">
              <button className="btn dark" onClick={() => scrollToSection("cta")}>
                Launch an IR Demo
              </button>
              <a className="btn" href="#flow">
                See the Lifecycle
              </a>
            </div>
            <div className="stat-ribbon">
              <div className="stat-pill">
                <strong>Display IR</strong> Shelf execution, OSA, OOS and MSL compliance
              </div>
              <div className="stat-pill">
                <strong>Receipt IR</strong> Transaction validation, extraction and normalization
              </div>
              <div className="stat-pill">
                <strong>Decision Engine</strong> Auto approve, auto reject or manual review
              </div>
            </div>
          </div>
          <div className="hero-stage">
            <div className="viewer">
              <div className="canvas-card">
                <div className="canvas-label">DISPLAY IR · Shelf Execution</div>
                <div className="shelf-scene">
                  <div
                    style={{
                      position: "absolute",
                      top: "14px",
                      left: "20px",
                      fontSize: "13px",
                      color: "#666",
                      fontWeight: 600,
                    }}
                  >
                    Transforms shelf images into measurable execution data
                  </div>
                  <div className="rack"></div>
                  <div className="shelf-items">
                    <div className="item-row">
                      <div className="sku"></div>
                      <div className="sku"></div>
                      <div className="sku"></div>
                      <div className="sku"></div>
                      <div className="sku"></div>
                      <div className="sku"></div>
                    </div>
                    <div className="item-row">
                      <div className="sku"></div>
                      <div className="sku"></div>
                      <div className="sku"></div>
                      <div className="sku"></div>
                      <div className="sku"></div>
                      <div className="sku"></div>
                    </div>
                    <div className="item-row">
                      <div className="sku"></div>
                      <div className="sku"></div>
                      <div className="sku"></div>
                      <div className="sku"></div>
                      <div className="sku"></div>
                      <div className="sku"></div>
                    </div>
                  </div>
                  <div className="detect-box box-a"></div>
                  <div className="detect-box box-b"></div>
                  <div className="detect-box box-c"></div>
                  <div className="result-chip chip-a">SKU Detected</div>
                  <div className="result-chip chip-b">Facing Count</div>
                  <div className="result-chip chip-c">MSL Check</div>
                </div>
              </div>

              <div className="canvas-card">
                <div className="canvas-label">RECEIPT IR · Transaction Validation</div>
                <div className="receipt-scene">
                  <div
                    style={{
                      position: "absolute",
                      top: "14px",
                      left: "20px",
                      fontSize: "13px",
                      color: "#666",
                      fontWeight: 600,
                    }}
                  >
                    Extracts and normalizes transaction data for rule validation
                  </div>
                  <div className="receipt-paper">
                    <div className="r-line short"></div>
                    <div className="r-line mid"></div>
                    <div className="r-line"></div>
                    <div className="r-line short"></div>
                    <div className="r-line mid"></div>
                    <div className="r-line"></div>
                    <div className="r-line short"></div>
                    <div className="r-line tiny"></div>
                  </div>
                  <div className="extract-box">
                    <strong>Extracted Fields</strong>
                    <div className="extract-item">
                      <span>Merchant</span>
                      <span>Valid</span>
                    </div>
                    <div className="extract-item">
                      <span>SKU Match</span>
                      <span>Mapped</span>
                    </div>
                    <div className="extract-item">
                      <span>Total</span>
                      <span>Verified</span>
                    </div>
                    <div className="extract-item">
                      <span>Confidence</span>
                      <span>98%</span>
                    </div>
                  </div>
                  <div className="validation-badge">Promo Validation Ready</div>
                </div>
              </div>
            </div>

            <div className="floating float-a">
              <strong>Architectural Intelligence</strong>
              <span>Computer vision + document intelligence + rules engine.</span>
            </div>
            <div className="floating float-b">
              <strong>Fraud Defense</strong>
              <span>Checks for image abuse, duplication, manipulation and invalid structures.</span>
            </div>
            <div className="floating float-c">
              <strong>Trusted Decisions</strong>
              <span>Structured outputs feed dashboards and automated decisions.</span>
            </div>

            <div className="signal-line line-a"></div>
            <div className="signal-line line-b"></div>
          </div>
        </section>

        <section className="wrap reveal" id="overview">
          <div className="head">
            <div>
              <div className="eyebrow">Architectural Intelligence Blueprint</div>
              <h2>
                Transforming unstructured field evidence into structured, auditable retail truth.
              </h2>
            </div>
            <p>
              At the core of Kartini is a system that transforms photos of shelves and receipts into
              structured retail intelligence. What starts as raw field evidence becomes measurable,
              explainable and auditable business data through computer vision, document intelligence
              and a rules engine.
            </p>
          </div>
          <div className="grid4">
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Input</div>
                <div className="metric-value">Unstructured evidence</div>
              </div>
              <p>Field photos, shelf images and receipts captured from real retail environments.</p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Engine</div>
                <div className="metric-value">Computer vision + document intelligence</div>
              </div>
              <p>Detection, OCR, prediction and contextual analysis work in parallel.</p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Rules</div>
                <div className="metric-value">Business logic validation</div>
              </div>
              <p>
                Campaign, store, SKU and promo rules determine whether evidence is operationally
                valid.
              </p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Output</div>
                <div className="metric-value">Structured retail intelligence</div>
              </div>
              <p>Results flow into dashboards and decision layers, not just image storage.</p>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal" id="engines">
          <div className="head">
            <div>
              <div className="eyebrow">The Dual Engines of Kartini</div>
              <h2>
                Display IR and Receipt IR validate the physical shelf and the financial transaction.
              </h2>
            </div>
            <p>
              Display IR focuses on shelf execution. Receipt IR focuses on transaction validation.
              Together, they form a complete promotional intelligence loop that supports both
              consumer and trade workflows.
            </p>
          </div>
          <div className="stack-compare">
            <div className="compare-card">
              <div className="compare-head">Display IR · Shelf Execution</div>
              <div className="compare-body">
                <p>
                  Transforms unstructured shelf images into measurable execution data such as OSA,
                  OOS, facings and MSL compliance.
                </p>
                <div className="list">
                  <div className="list-item">
                    <strong>Product Detection</strong>
                    <span>AI converts pixels into SKU presence and facing counts.</span>
                  </div>
                  <div className="list-item">
                    <strong>Shelf Context Analysis</strong>
                    <span>
                      Rows, blocks and grouping are evaluated to understand display context.
                    </span>
                  </div>
                  <div className="list-item">
                    <strong>Compliance Engine</strong>
                    <span>Results are checked against MSL and competitor shelf share rules.</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="compare-card">
              <div className="compare-head">Receipt IR · Transaction Validation</div>
              <div className="compare-body">
                <p>
                  Extracts and normalizes transaction data to validate consumer and trade promotions
                  using OCR, structure recognition and fuzzy SKU prediction.
                </p>
                <div className="list">
                  <div className="list-item">
                    <strong>Data Extraction</strong>
                    <span>Dates, merchant names, items and totals are captured from receipts.</span>
                  </div>
                  <div className="list-item">
                    <strong>SKU Normalisation</strong>
                    <span>Inconsistent item names are resolved into clean, standardized SKUs.</span>
                  </div>
                  <div className="list-item">
                    <strong>Validation Logic</strong>
                    <span>
                      Rules confirm campaign eligibility, store validity and transaction fit.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="wrap reveal" id="flow">
          <div className="head">
            <div>
              <div className="eyebrow">Execution & Transaction Lifecycle</div>
              <h2>How both engines convert images into decisions.</h2>
            </div>
            <p>
              Display IR and Receipt IR follow structured lifecycles that begin with submission and
              end with dashboards, operational follow-up or automated decisions.
            </p>
          </div>
          <div className="steps">
            <div className="step">
              <div className="step-num">1</div>
              <h3>Ingestion</h3>
              <p>Images enter through WhatsApp, Dompet Kartini, apps or API-connected channels.</p>
            </div>
            <div className="step">
              <div className="step-num">2</div>
              <h3>Enhancement</h3>
              <p>
                Preprocessing improves orientation, brightness, structural clarity and submission
                quality.
              </p>
            </div>
            <div className="step">
              <div className="step-num">3</div>
              <h3>Recognition</h3>
              <p>
                Object detection, OCR and fuzzy matching extract evidence from shelf and receipt
                images.
              </p>
            </div>
            <div className="step">
              <div className="step-num">4</div>
              <h3>Validation</h3>
              <p>
                Fraud signals and business rules evaluate whether evidence is trustworthy and
                eligible.
              </p>
            </div>
            <div className="step">
              <div className="step-num">5</div>
              <h3>Outcome</h3>
              <p>
                Results surface into dashboards, accept/reject logic and operational follow-up
                actions.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal">
          <div className="head">
            <div>
              <div className="eyebrow">Under The Hood</div>
              <h2>Two technical pipelines working simultaneously.</h2>
            </div>
            <p>
              Kartini runs parallel engines across image preprocessing, detection, analysis, fraud
              checks and business rules so shelf evidence and receipt evidence can both feed the
              same decision layer.
            </p>
          </div>
          <div className="grid2">
            <div className="panel">
              <h3>Display IR · Shelf Architecture</h3>
              <p>
                Image preprocessing corrects orientation, normalizes brightness and reduces noise.
                AI object detection produces SKU candidates, bounding boxes, facings and confidence
                scores. Shelf context analysis evaluates rows, blocks and product grouping. SKU
                mapping matches detections against master databases and brand tables, while the
                compliance engine evaluates MSL and competitor shelf share against rules.
              </p>
            </div>
            <div className="panel">
              <h3>Receipt IR · Document Intelligence</h3>
              <p>
                Enhancement improves receipt quality for OCR. Structural layout extraction
                identifies headers, timestamps and mathematical totals, not just flat text. A fuzzy
                matching and prediction engine resolves chaotic input labels into clean SKUs, making
                transaction data usable for validation and analytics.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap reveal">
          <div className="head">
            <div>
              <div className="eyebrow">Unified Architecture Map</div>
              <h2>Both engines feed the same fraud, rules and decision layer.</h2>
            </div>
            <p>
              Whether the input is a shelf image or a receipt, Kartini routes it through a common
              validation architecture that includes fraud detection, business rules and
              dashboard-ready outputs. This makes the system consistent, explainable and
              operationally auditable.
            </p>
          </div>
          <div className="grid3">
            <div className="panel">
              <h3>Parallel Engines</h3>
              <p>
                Display analysis and receipt analysis operate independently, but are joined by
                common fraud and rule enforcement layers.
              </p>
            </div>
            <div className="panel">
              <h3>Business Rules</h3>
              <p>
                MSL compliance, promo eligibility, store validation and campaign logic are all
                enforced before a decision is made.
              </p>
            </div>
            <div className="panel">
              <h3>Decision & Dashboards</h3>
              <p>
                Accepted, rejected or flagged evidence is surfaced into operational workflows and
                reporting environments.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal">
          <div className="head">
            <div>
              <div className="eyebrow">Securing The Shelf & Transaction</div>
              <h2>Fraud defense is built into the architecture, not added afterward.</h2>
            </div>
            <p>
              Kartini protects Display IR and Receipt IR with multiple technical countermeasures
              designed to detect image abuse, duplicate submissions, synthetic manipulation and
              invalid evidence structures.
            </p>
          </div>
          <div className="defense-grid">
            <div className="defense-card">
              <h3>Display Abuse Vectors</h3>
              <div className="defense-table">
                <div className="defense-row">
                  <strong>Fake Store Check-in</strong>
                  <span>GPS verification and time/submission correlation.</span>
                </div>
                <div className="defense-row">
                  <strong>Reused Display Image</strong>
                  <span>Image fingerprinting and perceptual hash comparison.</span>
                </div>
                <div className="defense-row">
                  <strong>Screen Photo Recapture</strong>
                  <span>Screen reflection and moire pattern detection.</span>
                </div>
                <div className="defense-row">
                  <strong>Selective Cropping</strong>
                  <span>Shelf context detection and minimum coverage rules.</span>
                </div>
                <div className="defense-row">
                  <strong>Edited / Synthetic Image</strong>
                  <span>AI anomaly detection and lighting consistency checks.</span>
                </div>
              </div>
            </div>
            <div className="defense-card">
              <h3>Receipt Manipulation</h3>
              <div className="defense-table">
                <div className="defense-row">
                  <strong>Duplicate Submissions</strong>
                  <span>Hash matching, timestamps and line-item similarity scoring.</span>
                </div>
                <div className="defense-row">
                  <strong>AI-Generated Receipts</strong>
                  <span>Layout validation against known merchant formats.</span>
                </div>
                <div className="defense-row">
                  <strong>Physical Marker Edits</strong>
                  <span>Overwritten text detection and color anomaly scanning.</span>
                </div>
                <div className="defense-row">
                  <strong>Invalid Document Structure</strong>
                  <span>Mandatory header detection and completeness scoring.</span>
                </div>
                <div className="defense-row">
                  <strong>Valid Receipt, Invalid Claim</strong>
                  <span>SKU eligibility verification and promo window validation.</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="wrap reveal">
          <div className="head">
            <div>
              <div className="eyebrow">5 Stage Validation Pipeline</div>
              <h2>Every submission moves through a risk-aware decision funnel.</h2>
            </div>
            <p>
              Before an image becomes an operational decision, it passes through five stages that
              test quality, extract evidence, analyze fraud signals, validate business rules and
              combine all signals into a final risk profile.
            </p>
          </div>
          <div className="funnel">
            <div className="funnel-visual">
              <div className="cone"></div>
              <div className="beam"></div>
            </div>
            <div className="funnel-steps">
              <div className="funnel-item">
                <strong>1. Submission Quality</strong>
                <span>Rejecting corrupt or blurry files at entry.</span>
              </div>
              <div className="funnel-item">
                <strong>2. Evidence Extraction</strong>
                <span>OCR and object detection surface usable evidence.</span>
              </div>
              <div className="funnel-item">
                <strong>3. Fraud Signal Analysis</strong>
                <span>Duplication, manipulation and anomaly indicators are scored.</span>
              </div>
              <div className="funnel-item">
                <strong>4. Business Rule Validation</strong>
                <span>Campaign logic, store validity and SKU eligibility are checked.</span>
              </div>
              <div className="funnel-item">
                <strong>5. Risk Scoring</strong>
                <span>
                  AI confidence, fraud indicators and rule outcomes combine into a final profile.
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal">
          <div className="head">
            <div>
              <div className="eyebrow">The Decision Engine</div>
              <h2>Automated outcomes with retained audit logic.</h2>
            </div>
            <p>
              Kartini's decision engine determines whether evidence should be automatically
              accepted, automatically rejected or routed into manual review depending on confidence,
              fraud signals and rule compliance.
            </p>
          </div>
          <div className="decision-grid">
            <div className="decision-card">
              <div className="decision-top green">Auto Approve</div>
              <div className="decision-body">
                <strong>Criteria</strong>
                <p>High AI confidence and full business rule compliance.</p>
                <strong style={{ marginTop: "12px" }}>Outcome</strong>
                <p>Immediate acceptance and dashboard reflection.</p>
              </div>
            </div>
            <div className="decision-card">
              <div className="decision-top red">Auto Reject</div>
              <div className="decision-body">
                <strong>Criteria</strong>
                <p>Confirmed fraud signal or hard business rule violation.</p>
                <strong style={{ marginTop: "12px" }}>Outcome</strong>
                <p>Immediate rejection with retained audit log.</p>
              </div>
            </div>
            <div className="decision-card">
              <div className="decision-top amber">Manual Review</div>
              <div className="decision-body">
                <strong>Criteria</strong>
                <p>Mixed signals or low confidence scores.</p>
                <strong style={{ marginTop: "12px" }}>Outcome</strong>
                <p>Queued for human review with extracted evidence surfaced.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="wrap reveal">
          <div className="head">
            <div>
              <div className="eyebrow">The Differentiator</div>
              <h2>Why simple AI fails and Kartini goes further.</h2>
            </div>
            <p>
              Simple image recognition usually stops at detection, making it vulnerable to
              manipulation, false positives and weak explainability. Kartini adds master data
              alignment, fraud signals, business rules and historical learning so evidence becomes
              trusted operational decisions.
            </p>
          </div>
          <div className="grid4">
            <div className="panel">
              <h3>Image AI</h3>
              <p>Computer vision and OCR detect visual and document signals.</p>
            </div>
            <div className="panel">
              <h3>Master Data</h3>
              <p>
                Campaign and SKU alignment ensure evidence is interpreted against the right
                reference set.
              </p>
            </div>
            <div className="panel">
              <h3>Fraud Defense</h3>
              <p>Manipulation checks prevent false trust in altered or duplicate submissions.</p>
            </div>
            <div className="panel">
              <h3>Business Rules</h3>
              <p>
                Eligibility enforcement turns extracted signals into decisions that matter
                commercially.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal">
          <div className="head">
            <div>
              <div className="eyebrow">Actionable Intelligence at Scale</div>
              <h2>Real-time visibility that can trigger corrective action.</h2>
            </div>
            <p>
              The value of IR is not just validation. It is the ability to surface out-of-stock
              hotspots, MSL gaps, distributor issues, campaign ROI and regional execution patterns
              quickly enough for brands to act on them.
            </p>
          </div>
          <div className="grid3">
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Visibility</div>
                <div className="metric-value">Real-time OOS and MSL insights</div>
              </div>
              <p>Identify problem areas regionally or nationally as evidence is submitted.</p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Agility</div>
                <div className="metric-value">Operational follow-up</div>
              </div>
              <p>Trigger field corrections and distributor escalations based on hard evidence.</p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">ROI</div>
                <div className="metric-value">Campaign truth with auditability</div>
              </div>
              <p>Measure effectiveness through trusted transaction and retail execution data.</p>
            </div>
          </div>
        </section>

        <section className="wrap reveal" id="trust">
          <div className="head">
            <div>
              <div className="eyebrow">Operational Trust, Engineered</div>
              <h2>Kartini is not simply AI image detection.</h2>
            </div>
            <p>
              Kartini combines computer vision, document intelligence, master data, fraud defense
              and business rules to create trusted retail execution. The result is evidence that can
              be defended, explained and operationally relied upon at every step.
            </p>
          </div>
          <div className="trust-strip">
            <div className="trust-node">Computer Vision</div>
            <div className="trust-node">Document Intelligence</div>
            <div className="trust-node">Master Data</div>
            <div className="trust-node">Fraud Defense</div>
            <div className="trust-node">Business Rules</div>
            <div className="trust-node" style={{ background: "var(--navy)", color: "#fff" }}>
              Trusted Retail Execution
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal" id="cta">
          <div className="cta">
            <div className="eyebrow">Get Started</div>
            <h2>Build IR systems that produce trusted, explainable retail decisions.</h2>
            <p>
              Kartini enables brands to deploy Display IR and Receipt IR as operational engines -
              not just image detection layers - turning shelf and transaction evidence into
              auditable intelligence that supports promotions, retail execution and reporting at
              scale.
            </p>
            <div className="hero-actions">
              <button className="btn dark">Book an IR Demo</button>
              <button className="btn">Explore the Kartini Platform</button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default DisplayReceiptIr;
